/**
 * ConsentBridge — syncs Cookiebot consent state into PostHog.
 *
 * Posture: US/CCPA opt-out (Territory=United States, banner OFF).
 *   - PostHog captures by default (opt-out model).
 *   - We opt the user OUT of PostHog only when Cookiebot reports that the
 *     `statistics` category is declined — which happens when the user uses the
 *     "Your Privacy Choices" footer link to opt out, OR arrives with GPC on
 *     (Cookiebot auto-declines for GPC visitors).
 *   - If the user later opts back in, we re-enable capture.
 *
 * Why a bridge (vs. data-blockingmode="auto" alone): PostHog is initialized by
 * the React SDK (<PostHogProvider>), not by a <script> tag Cookiebot can block.
 * Auto-blocking can't gate it, so we gate it explicitly here.
 *
 * Marketing category (Meta Pixel via GTM / direct fbq) is governed separately
 * by Cookiebot auto-blocking + Consent Mode — not by this component.
 *
 * This renders nothing; it only wires up event listeners.
 */

import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import { wasPrivacyDialogOpened } from '../utils/consent';

/** Cookiebot fires this on the window each time consent is determined/changed. */
const CONSENT_EVENTS = [
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
] as const;

/**
 * Grace period before we treat a missing `window.Cookiebot` as a load failure.
 * Cookiebot's loader (uc.js) is async; 5s is comfortably past a normal load.
 */
const COOKIEBOT_LOAD_TIMEOUT_MS = 5000;

/**
 * sessionStorage key that de-dupes the `consent_opt_out` event across pageviews
 * within a single browser session. Without it, a GPC visitor (whom Cookiebot
 * auto-declines on every load) would emit one opt-out event per pageview. With
 * it, we capture exactly one opt-out per session — which is what "did this user
 * opt out?" actually means.
 */
const OPT_OUT_SESSION_KEY = 'ga_consent_opt_out_reported';

export default function ConsentBridge(): null {
  const posthog = usePostHog();
  // The `consent_state` telemetry event must fire at most once per mount.
  const reportedRef = useRef(false);

  useEffect(() => {
    // Resolve the PostHog instance robustly: prefer the React-context instance
    // from usePostHog(), but fall back to the global `window.posthog` singleton
    // that posthog-js installs on init. This mirrors how useTracking captures
    // pageview/purchase (posthogRef.current || window.posthog). Without the
    // fallback, when Cookiebot resolves consent before usePostHog() is populated
    // in this component, the old `if (!posthog) return;` guard bailed the whole
    // effect and `consent_state` was silently dropped — which is exactly why it
    // never landed in PostHog while pageviews (which have the fallback) did.
    const getPosthog = () =>
      posthog ?? (typeof window !== 'undefined' ? (window as any).posthog : null) ?? null;

    // #1 — Emit a one-time telemetry event describing the consent posture.
    //
    // ISOLATION BY DESIGN: this is a pure capture() with event-local
    // properties only. It does NOT call register()/super-properties or
    // identify(), so it cannot alter the payload of any other event
    // (pageview/purchase/etc.). That guarantee is what makes it safe; the
    // identifier-coverage baseline (CONSENT_BANNER_PLAN.md §11.2) is the
    // backstop that confirms we held the line.
    //
    // Why it matters: lets us alert if `method` ever flips off "implied"
    // (the one silent change that would kill US tracking) and track opt-out rate.
    const reportConsentState = (cookiebotLoaded: boolean) => {
      if (reportedRef.current) return;
      const ph = getPosthog();
      // No instance available yet — do NOT burn the once-per-mount flag; let the
      // next consent event (or the effect re-run when usePostHog populates) retry.
      if (!ph) return;
      reportedRef.current = true;
      const consent = window.Cookiebot?.consent;
      try {
        ph.capture('consent_state', {
          method: consent?.method ?? null,
          statistics: consent?.statistics ?? null,
          marketing: consent?.marketing ?? null,
          cookiebot_loaded: cookiebotLoaded,
        });
      } catch (err) {
        // Telemetry must never break the app — but surface the failure.
        Sentry.captureException(err);
      }
    };

    // Capture the actual opt-out ACTION (statistics declined) as a discrete
    // event, deduped to once per browser session. This is the metric of record
    // for "how many people opted out": the deliberate footer → "do not sell"
    // click (source: 'footer_click') plus GPC visitors auto-declined by
    // Cookiebot (source: 'gpc_auto'). Everyone else is opted in by default.
    //
    // ISOLATION BY DESIGN: pure capture() with event-local props — no
    // register()/super-properties, no identify() — so it can't touch any other
    // event's payload (pageview/purchase). Does NOT go through GTM, so the Pixel
    // / CAPI conversion path is untouched.
    const reportOptOut = () => {
      const ph = getPosthog();
      if (!ph) return; // no instance yet — a later sync() (e.g. on decline) retries

      // Session-level de-dupe so a GPC visitor doesn't emit one per pageview.
      try {
        if (sessionStorage.getItem(OPT_OUT_SESSION_KEY)) return;
      } catch {
        // sessionStorage unavailable (private mode / blocked) — fall through and
        // capture; at worst we over-count slightly rather than miss the opt-out.
      }

      const cb = window.Cookiebot;
      const gpc = cb?.doNotTrack === true;
      // Attribute the source: a deliberate footer click takes precedence; absent
      // that, a GPC browser signal; otherwise unknown (shouldn't normally occur).
      const source = wasPrivacyDialogOpened()
        ? 'footer_click'
        : gpc
          ? 'gpc_auto'
          : 'unknown';

      try {
        ph.capture('consent_opt_out', {
          source,
          gpc,
          marketing: cb?.consent?.marketing ?? null,
          statistics: cb?.consent?.statistics ?? null,
        });
        try {
          sessionStorage.setItem(OPT_OUT_SESSION_KEY, '1');
        } catch {
          /* best-effort dedupe; ignore storage failures */
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    };

    const sync = () => {
      const cb = window.Cookiebot;
      // Opt-out model: if Cookiebot hasn't loaded yet, assume consent (default-on).
      const statisticsConsent = cb?.consent?.statistics ?? true;
      const ph = getPosthog();

      try {
        if (ph) {
          if (statisticsConsent) {
            if (ph.has_opted_out_capturing?.()) {
              ph.opt_in_capturing?.();
            }
          } else {
            if (!ph.has_opted_out_capturing?.()) {
              ph.opt_out_capturing?.();
            }
          }
        }
      } catch (err) {
        // #3 — Never let consent wiring break the app, but surface a broken
        // bridge as a real Sentry issue instead of a silent swallow.
        Sentry.captureException(err);
      }

      // Statistics declined = the user is opted out. Capture the action (deduped).
      if (cb?.consent && !statisticsConsent) reportOptOut();

      // Once Cookiebot has actually resolved consent, emit the telemetry event.
      if (cb?.consent) reportConsentState(true);
    };

    // Run once on mount in case Cookiebot already resolved consent before we
    // attached listeners (race: CookiebotOnConsentReady may have already fired).
    sync();

    CONSENT_EVENTS.forEach((evt) => window.addEventListener(evt, sync));

    // #3 — Load-failure tripwire. If Cookiebot never loads, that is a silent
    // failure (no JS exception): the footer opt-out link is dead and consent is
    // unenforced. Surface it to Sentry, and emit consent_state with
    // cookiebot_loaded:false so the failure is visible in PostHog too.
    const loadTimer = window.setTimeout(() => {
      if (!window.Cookiebot) {
        Sentry.captureMessage('cookiebot_failed_to_load', 'warning');
        reportConsentState(false);
      }
    }, COOKIEBOT_LOAD_TIMEOUT_MS);

    return () => {
      CONSENT_EVENTS.forEach((evt) => window.removeEventListener(evt, sync));
      window.clearTimeout(loadTimer);
    };
  }, [posthog]);

  return null;
}
